import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePlayer } from './PlayerContext';
import './PlaylistPage.css';

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M8 5v14l11-7z"/></svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
);
const ShuffleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/>
  </svg>
);

function PlaylistPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tracks, currentIndex, isPlaying, playTrack, setIsPlaying, shuffleOn, setShuffleOn, togglePlayPause } = usePlayer();
  const [menuOpen, setMenuOpen] = useState(null);
  const menuRef = useRef(null);

  // Handle shared link: ?track=N — opens playlist and starts that track
  useEffect(() => {
    const trackParam = searchParams.get('track');
    if (trackParam && tracks.length) {
      const idx = parseInt(trackParam, 10);
      if (!isNaN(idx) && idx >= 0 && idx < tracks.length) {
        playTrack(idx);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, tracks.length, playTrack, setSearchParams]);

  const handleRowClick = (i) => {
    if (i === currentIndex) {
      togglePlayPause();
    } else {
      playTrack(i);
    }
  };

  const handleShare = async (e, i) => {
    e.stopPropagation();
    setMenuOpen(null);
    const url = `${window.location.origin}/music/playlist?track=${i}`;
    const track = tracks[i];
    const text = track ? `${track.title} — ${track.artist}` : '';

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'ferl playlist',
          text,
          url,
        });
      } catch (err) {
        if (err.name !== 'AbortError') copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(() => {});
  };

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const handlePlayPause = () => {
    if (!tracks.length) return;
    if (isPlaying) {
      setIsPlaying(false);
    } else {
      const idx = currentIndex >= 0 && currentIndex < tracks.length ? currentIndex : 0;
      playTrack(idx);
    }
  };

  const handleShuffle = () => {
    setShuffleOn((p) => !p);
    if (tracks.length && !isPlaying) {
      const idx = Math.floor(Math.random() * tracks.length);
      playTrack(idx);
    }
  };

  return (
    <div className="playlist-page">
      {/* Hero: album art + artist + Play & Shuffle */}
      <header className="playlist-hero">
        <div className="playlist-hero-art-wrap">
          <img src="/playlist-thumbnail.png" alt="Playlist" className="playlist-hero-art" />
        </div>
        <div className="playlist-hero-info">
          <div className="playlist-hero-buttons">
            <button
              type="button"
              className="playlist-hero-btn playlist-hero-play"
              onClick={handlePlayPause}
              disabled={!tracks.length}
            >
              {isPlaying ? <><PauseIcon /> Pause</> : <><PlayIcon /> Play</>}
            </button>
            <button
              type="button"
              className={`playlist-hero-btn playlist-hero-shuffle${shuffleOn ? ' active' : ''}`}
              onClick={handleShuffle}
              disabled={!tracks.length}
            >
              <ShuffleIcon /> Shuffle
            </button>
          </div>
        </div>
      </header>

      {/* Track list — alternating rows */}
      <section className="playlist-tracks">
        {tracks.length === 0 ? (
          <p className="playlist-empty">No tracks yet.</p>
        ) : (
          <ul className="playlist-list">
            {tracks.map((track, i) => {
              const isThisPlaying = i === currentIndex && isPlaying;
              const isThisActive = i === currentIndex;
              const isEven = i % 2 === 0;
              return (
                <li
                  key={i}
                  className={`playlist-item ${isEven ? 'even' : 'odd'}${isThisActive ? ' active' : ''}`}
                  onClick={() => handleRowClick(i)}
                >
                  <div className="playlist-num-wrap">
                    {isThisPlaying ? (
                      <span className="playlist-playing-icon"><PauseIcon /></span>
                    ) : isThisActive ? (
                      <span className="playlist-playing-icon"><PlayIcon /></span>
                    ) : (
                      <>
                        <span className="playlist-num">{i + 1}</span>
                        <span className="playlist-play-hint" aria-hidden><PlayIcon /></span>
                      </>
                    )}
                  </div>
                  <div className="playlist-info">
                    <span className="playlist-title">{track.title}</span>
                  </div>
                  <div
                    className="playlist-kebab-wrap"
                    ref={menuOpen === i ? menuRef : null}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="playlist-kebab"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen((prev) => (prev === i ? null : i));
                      }}
                      aria-label="Options"
                      aria-expanded={menuOpen === i}
                    >
                      <span aria-hidden>⋯</span>
                    </button>
                    {menuOpen === i && (
                      <div className="playlist-menu">
                        <button type="button" onClick={(e) => handleShare(e, i)}>
                          Share
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

export default PlaylistPage;
